import type { OfflineQueueItem } from '@/types';

class OfflineService {
  private readonly STORAGE_KEY = 'offline_queue';
  private readonly CACHE_VERSION = '1.0';
  private readonly MAX_QUEUE_SIZE = 100;
  private readonly MAX_CACHE_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days
  
  private isOnline = navigator.onLine;
  private syncInProgress = false;

  constructor() {
    this.initializeOfflineHandling();
  }

  // ---------- Safe localStorage access ----------
  private getStorage(): Storage | null {
    try {
      if (typeof window === 'undefined') return null;
      return window.localStorage;
    } catch {
      return null;
    }
  }

  private initializeOfflineHandling(): void {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncOfflineData();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Sync on page load if online
    if (this.isOnline) {
      this.syncOfflineData();
    }

    // Clean up old cache entries on initialization
    this.cleanupOldCache();
  }

  // ---------- Enhanced Queue Management ----------
  // Get offline queue with validation and cleanup
  private getOfflineQueue(): OfflineQueueItem[] {
    const storage = this.getStorage();
    if (!storage) return [];

    try {
      const stored = storage.getItem(this.STORAGE_KEY);
      if (!stored) return [];

      const queueData = JSON.parse(stored);
      
      // Version check
      if (queueData.version !== this.CACHE_VERSION) {
        storage.removeItem(this.STORAGE_KEY);
        return [];
      }

      // Data validation
      if (!Array.isArray(queueData.items)) {
        storage.removeItem(this.STORAGE_KEY);
        return [];
      }

      // Filter out expired items (older than MAX_CACHE_AGE)
      const now = Date.now();
      const validItems = queueData.items.filter((item: OfflineQueueItem) => {
        const itemAge = now - new Date(item.timestamp).getTime();
        return itemAge < this.MAX_CACHE_AGE;
      });

      // If items were filtered out, save the cleaned queue
      if (validItems.length !== queueData.items.length) {
        this.saveOfflineQueue(validItems);
      }

      return validItems;
    } catch (error) {
      console.warn('Failed to read offline queue, clearing corrupted data:', error);
      storage.removeItem(this.STORAGE_KEY);
      return [];
    }
  }

  // Save offline queue with version control and size management
  private saveOfflineQueue(queue: OfflineQueueItem[]): void {
    const storage = this.getStorage();
    if (!storage) return;

    try {
      // Enforce max queue size (LRU eviction)
      let managedQueue = queue;
      if (queue.length > this.MAX_QUEUE_SIZE) {
        // Sort by timestamp and keep the most recent items
        managedQueue = queue
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, this.MAX_QUEUE_SIZE);
        
        console.warn(`Offline queue size exceeded ${this.MAX_QUEUE_SIZE}, keeping ${managedQueue.length} most recent items`);
      }

      const queueData = {
        version: this.CACHE_VERSION,
        items: managedQueue,
        lastUpdated: Date.now()
      };

      storage.setItem(this.STORAGE_KEY, JSON.stringify(queueData));
    } catch (error) {
      console.warn('Failed to save offline queue:', error);
    }
  }

  // Check if device is online
  getOnlineStatus(): boolean {
    return this.isOnline;
  }

  // Add item to offline queue with enhanced validation
  async queueOfflineAction(
    type: OfflineQueueItem['type'],
    data: any,
    maxRetries: number = 3
  ): Promise<void> {
    if (!type || !data) {
      console.warn('Invalid offline action: missing type or data');
      return;
    }

    const item: OfflineQueueItem = {
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: new Date(),
      retryCount: 0,
      maxRetries: Math.max(1, Math.min(maxRetries, 10)) // Clamp between 1-10
    };

    const queue = this.getOfflineQueue();
    queue.push(item);
    this.saveOfflineQueue(queue);

    console.log(`📝 Queued offline action: ${type} (queue size: ${queue.length})`);

    // If online, try to sync immediately
    if (this.isOnline) {
      await this.syncOfflineData();
    }
  }

  // Sync offline data when back online with enhanced error handling
  async syncOfflineData(): Promise<void> {
    if (!this.isOnline || this.syncInProgress) {
      return;
    }

    this.syncInProgress = true;
    const queue = this.getOfflineQueue();
    
    if (queue.length === 0) {
      this.syncInProgress = false;
      return;
    }

    console.log(`🔄 Syncing ${queue.length} offline items...`);
    const successfulItems: string[] = [];
    let syncedCount = 0;

    for (const item of queue) {
      try {
        await this.processOfflineItem(item);
        successfulItems.push(item.id);
        syncedCount++;
        console.log(`✅ Synced offline item: ${item.type} (${syncedCount}/${queue.length})`);
      } catch (error) {
        console.error('❌ Failed to sync offline item:', item.type, error);
        
        // Increment retry count
        item.retryCount++;
        
        // Remove if max retries exceeded
        if (item.retryCount >= item.maxRetries) {
          successfulItems.push(item.id);
          console.warn(`⚠️ Max retries exceeded for offline item: ${item.type} (${item.id})`);
        }
      }
    }

    // Remove successfully processed items
    const remainingQueue = queue.filter(item => !successfulItems.includes(item.id));
    this.saveOfflineQueue(remainingQueue);
    
    if (syncedCount > 0) {
      console.log(`✅ Offline sync completed: ${syncedCount} items synced, ${remainingQueue.length} remaining`);
    }
    
    this.syncInProgress = false;
  }

  // Process individual offline item with better error handling
  private async processOfflineItem(item: OfflineQueueItem): Promise<void> {
    if (!item || !item.type || !item.data) {
      throw new Error('Invalid offline item structure');
    }

    switch (item.type) {
      case 'JOB_UPDATE':
        await this.syncJobUpdate(item.data);
        break;
      case 'MESSAGE_SEND':
        await this.syncMessage(item.data);
        break;
      case 'STATUS_CHANGE':
        await this.syncStatusChange(item.data);
        break;
      case 'PHOTO_UPLOAD':
        await this.syncPhotoUpload(item.data);
        break;
      default:
        console.warn('Unknown offline item type:', item.type);
        throw new Error(`Unsupported offline action type: ${item.type}`);
    }
  }

  // Sync job update with validation
  private async syncJobUpdate(data: any): Promise<void> {
    if (!data || !data.jobId) {
      throw new Error('Invalid job update data: missing jobId');
    }

    console.log('🔄 Syncing job update:', data.jobId);
    
    // Simulate API call with timeout
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Job update sync timeout')), 10000);
      setTimeout(() => {
        clearTimeout(timeout);
        resolve(undefined);
      }, 1000);
    });
    
    // Update local storage with synced data
    const storage = this.getStorage();
    if (storage) {
      try {
        const jobs = JSON.parse(storage.getItem('jobs') || '[]');
        const jobIndex = jobs.findIndex((j: any) => j.id === data.jobId);
        if (jobIndex !== -1) {
          jobs[jobIndex] = { ...jobs[jobIndex], ...data.updates, lastSynced: new Date().toISOString() };
          storage.setItem('jobs', JSON.stringify(jobs));
        }
      } catch (error) {
        console.warn('Failed to update local job storage:', error);
      }
    }
  }

  // Sync message with validation
  private async syncMessage(data: any): Promise<void> {
    if (!data || !data.chatId || !data.messageId) {
      throw new Error('Invalid message data: missing chatId or messageId');
    }

    console.log('💬 Syncing message:', data.messageId);
    
    // Simulate API call with timeout
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Message sync timeout')), 8000);
      setTimeout(() => {
        clearTimeout(timeout);
        resolve(undefined);
      }, 500);
    });
    
    // Update message status to sent
    const storage = this.getStorage();
    if (storage) {
      try {
        const chats = JSON.parse(storage.getItem('chats') || '[]');
        const chat = chats.find((c: any) => c.id === data.chatId);
        if (chat) {
          const message = chat.messages.find((m: any) => m.id === data.messageId);
          if (message) {
            message.status = 'sent';
            message.timestamp = new Date();
            message.lastSynced = new Date().toISOString();
            storage.setItem('chats', JSON.stringify(chats));
          }
        }
      } catch (error) {
        console.warn('Failed to update local chat storage:', error);
      }
    }
  }

  // Sync status change with validation
  private async syncStatusChange(data: any): Promise<void> {
    if (!data || !data.type) {
      throw new Error('Invalid status change data: missing type');
    }

    console.log('🔄 Syncing status change:', data.type);
    
    // Simulate API call with timeout
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Status change sync timeout')), 8000);
      setTimeout(() => {
        clearTimeout(timeout);
        resolve(undefined);
      }, 800);
    });
    
    // Update status in local storage
    const storage = this.getStorage();
    if (storage && data.type === 'availability' && data.providerId) {
      try {
        const providers = JSON.parse(storage.getItem('providers') || '[]');
        const provider = providers.find((p: any) => p.id === data.providerId);
        if (provider) {
          provider.availabilityStatus = data.status;
          provider.lastSynced = new Date().toISOString();
          storage.setItem('providers', JSON.stringify(providers));
        }
      } catch (error) {
        console.warn('Failed to update local provider storage:', error);
      }
    }
  }

  // Sync photo upload with validation
  private async syncPhotoUpload(data: any): Promise<void> {
    if (!data || !data.jobId || !data.photoId) {
      throw new Error('Invalid photo upload data: missing jobId or photoId');
    }

    console.log('📸 Syncing photo upload:', data.photoId);
    
    // Simulate photo upload with timeout
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Photo upload sync timeout')), 15000);
      setTimeout(() => {
        clearTimeout(timeout);
        resolve(undefined);
      }, 2000);
    });
    
    // Update with uploaded URL
    const storage = this.getStorage();
    if (storage) {
      try {
        const jobs = JSON.parse(storage.getItem('jobs') || '[]');
        const job = jobs.find((j: any) => j.id === data.jobId);
        if (job) {
          job.images = job.images || [];
          job.images.push(`https://example.com/uploads/${data.photoId}.jpg`);
          job.lastSynced = new Date().toISOString();
          storage.setItem('jobs', JSON.stringify(jobs));
        }
      } catch (error) {
        console.warn('Failed to update local job images:', error);
      }
    }
  }

  // Get offline queue status with enhanced information
  getOfflineQueueStatus(): {
    itemCount: number;
    oldestItem?: Date;
    newestItem?: Date;
    isOnline: boolean;
    syncInProgress: boolean;
    failedItems: number;
  } {
    const queue = this.getOfflineQueue();
    let failedItems = 0;
    
    // Count items that have exceeded retry attempts
    queue.forEach(item => {
      if (item.retryCount >= item.maxRetries) {
        failedItems++;
      }
    });

    const timestamps = queue.map(item => new Date(item.timestamp));
    
    return {
      itemCount: queue.length,
      oldestItem: timestamps.length > 0 ? new Date(Math.min(...timestamps.map(d => d.getTime()))) : undefined,
      newestItem: timestamps.length > 0 ? new Date(Math.max(...timestamps.map(d => d.getTime()))) : undefined,
      isOnline: this.isOnline,
      syncInProgress: this.syncInProgress,
      failedItems
    };
  }

  // Clear offline queue (for testing/debugging)
  clearOfflineQueue(): void {
    const storage = this.getStorage();
    if (storage) {
      storage.removeItem(this.STORAGE_KEY);
      console.log('🗑️ Offline queue cleared');
    }
  }

  // ---------- Enhanced Caching Methods ----------
  // Cache essential data with version control and validation
  async cacheEssentialData(): Promise<void> {
    if (!this.isOnline) return;

    const storage = this.getStorage();
    if (!storage) return;

    try {
      // Cache user profile with validation
      const userProfile = JSON.parse(storage.getItem('user') || '{}');
      if (userProfile.id) {
        const cachedUserData = {
          data: userProfile,
          version: this.CACHE_VERSION,
          timestamp: Date.now()
        };
        storage.setItem('cached_user_profile', JSON.stringify(cachedUserData));
      }

      // Cache recent jobs with size limit
      const allJobs = JSON.parse(storage.getItem('jobs') || '[]');
      if (Array.isArray(allJobs)) {
        const recentJobs = allJobs
          .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
          .slice(0, 20); // Cache last 20 jobs
        
        const cachedJobsData = {
          data: recentJobs,
          version: this.CACHE_VERSION,
          timestamp: Date.now()
        };
        storage.setItem('cached_recent_jobs', JSON.stringify(cachedJobsData));
      }

      // Cache service categories with fallback data
      try {
        const categories = [
          { id: '1', name: 'Plumbing', icon: '🔧' },
          { id: '2', name: 'Electrical', icon: '⚡' },
          { id: '3', name: 'Cleaning', icon: '🧹' },
          { id: '4', name: 'HVAC', icon: '❄️' },
          { id: '5', name: 'Gardening', icon: '🌱' }
        ];
        
        const cachedCategoriesData = {
          data: categories,
          version: this.CACHE_VERSION,
          timestamp: Date.now()
        };
        storage.setItem('cached_categories', JSON.stringify(cachedCategoriesData));
      } catch (error) {
        console.warn('Could not cache categories for offline use:', error);
      }

      console.log('✅ Essential data cached for offline use');
    } catch (error) {
      console.error('❌ Failed to cache essential data:', error);
    }
  }

  // Get cached data with validation and expiry check
  getCachedData(type: 'user' | 'jobs' | 'categories'): any {
    const storage = this.getStorage();
    if (!storage) return null;

    try {
      const key = `cached_${type === 'user' ? 'user_profile' : type === 'jobs' ? 'recent_jobs' : 'categories'}`;
      const cached = storage.getItem(key);
      
      if (!cached) return null;

      const cacheData = JSON.parse(cached);

      // Version check
      if (cacheData.version !== this.CACHE_VERSION) {
        storage.removeItem(key);
        return null;
      }

      // Data validation
      if (!cacheData.data) {
        storage.removeItem(key);
        return null;
      }

      // Age check (7 days max)
      const now = Date.now();
      if (now - cacheData.timestamp > this.MAX_CACHE_AGE) {
        storage.removeItem(key);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.warn(`Failed to read cached ${type} data:`, error);
      return null;
    }
  }

  // ---------- Cache Cleanup ----------
  private cleanupOldCache(): void {
    const storage = this.getStorage();
    if (!storage) return;

    try {
      const cacheKeys = ['cached_user_profile', 'cached_recent_jobs', 'cached_categories'];
      const now = Date.now();

      cacheKeys.forEach(key => {
        try {
          const cached = storage.getItem(key);
          if (cached) {
            const cacheData = JSON.parse(cached);
            
            // Remove if version mismatch or too old
            if (
              cacheData.version !== this.CACHE_VERSION ||
              (cacheData.timestamp && now - cacheData.timestamp > this.MAX_CACHE_AGE)
            ) {
              storage.removeItem(key);
              console.log(`🗑️ Cleaned up old cache: ${key}`);
            }
          }
        } catch (error) {
          // Remove corrupted cache entries
          storage.removeItem(key);
          console.warn(`🗑️ Removed corrupted cache: ${key}`);
        }
      });
    } catch (error) {
      console.warn('Failed to cleanup old cache:', error);
    }
  }

  // Clear all offline cache
  clearOfflineCache(): void {
    const storage = this.getStorage();
    if (!storage) return;

    try {
      const cacheKeys = [
        this.STORAGE_KEY,
        'cached_user_profile',
        'cached_recent_jobs',
        'cached_categories'
      ];

      cacheKeys.forEach(key => storage.removeItem(key));
      console.log('🗑️ All offline cache cleared');
    } catch (error) {
      console.warn('Failed to clear offline cache:', error);
    }
  }

  // Get cache status
  getCacheStatus(): {
    queueSize: number;
    cacheAge: { user?: number; jobs?: number; categories?: number };
    totalCacheSize: number;
  } {
    const storage = this.getStorage();
    if (!storage) {
      return { queueSize: 0, cacheAge: {}, totalCacheSize: 0 };
    }

    const queue = this.getOfflineQueue();
    const cacheAge: { user?: number; jobs?: number; categories?: number } = {};
    let totalSize = 0;

    // Calculate cache ages and sizes
    const cacheKeys = [
      { key: 'cached_user_profile', type: 'user' as const },
      { key: 'cached_recent_jobs', type: 'jobs' as const },
      { key: 'cached_categories', type: 'categories' as const }
    ];

    cacheKeys.forEach(({ key, type }) => {
      try {
        const cached = storage.getItem(key);
        if (cached) {
          totalSize += cached.length;
          const cacheData = JSON.parse(cached);
          if (cacheData.timestamp) {
            cacheAge[type] = Date.now() - cacheData.timestamp;
          }
        }
      } catch {
        // Ignore errors
      }
    });

    return {
      queueSize: queue.length,
      cacheAge,
      totalCacheSize: totalSize
    };
  }
}

export const offlineService = new OfflineService();